import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface MediaGalleryItem {
  url?: string;
  publicUrl?: string;
  type?: string;
  isPrimary?: boolean;
  image?: {
    publicUrl?: string;
  };
}

export interface MediaGalleryProps {
  images?: (string | MediaGalleryItem)[];
  approvalStatus?: string;
  statusBadgeText?: string;
  fallbackIcon?: React.ReactNode;
  aspectRatioClass?: string;
  className?: string;
}

export function MediaGallery({
  images = [],
  approvalStatus,
  statusBadgeText,
  fallbackIcon = "🏪",
  aspectRatioClass = "h-64 md:h-80",
  className,
}: MediaGalleryProps) {
  const [imgIdx, setImgIdx] = useState(0);

  const formattedImages: { url: string; type?: string; isPrimary?: boolean }[] = images
    .map((img) => {
      if (typeof img === "string") return { url: img };
      if (!img) return null;
      const url = img.image?.publicUrl || img.publicUrl || img.url;
      if (!url) return null;
      return {
        url,
        type: img.type,
        isPrimary: img.isPrimary,
      };
    })
    .filter((item): item is { url: string; type?: string; isPrimary?: boolean } => Boolean(item));

  if (!formattedImages.length) {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <div
          className={cn("w-full flex items-center justify-center text-7xl opacity-25", aspectRatioClass)}
          style={{ background: "var(--brand-pale)" }}
        >
          {fallbackIcon}
        </div>
      </Card>
    );
  }

  const currentImg = formattedImages[imgIdx] || formattedImages[0];

  // Resolve status badge
  let badgeContent = null;
  if (statusBadgeText) {
    badgeContent = statusBadgeText;
  } else if (approvalStatus === "approved") {
    badgeContent = "✓ Đã duyệt";
  } else if (approvalStatus === "pending") {
    badgeContent = "● Chờ duyệt";
  }

  return (
    <Card className={cn("overflow-hidden", className)}>
      <div className={cn("relative w-full bg-gray-100", aspectRatioClass)}>
        <img
          src={currentImg.url}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "";
          }}
        />

        {/* Carousel controls if > 1 images */}
        {formattedImages.length > 1 && (
          <>
            <button
              onClick={() =>
                setImgIdx((i) => (i - 1 + formattedImages.length) % formattedImages.length)
              }
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setImgIdx((i) => (i + 1) % formattedImages.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {formattedImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === imgIdx ? "w-5 bg-white" : "w-1.5 bg-white/50"
                  )}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Status badge top-left */}
        {badgeContent && (
          <div className="absolute top-3 left-3">
            <span
              className={cn(
                "fnb-tag text-[11px] font-semibold",
                approvalStatus === "approved" ? "fnb-tag-green" : "fnb-tag-brand"
              )}
            >
              {badgeContent}
            </span>
          </div>
        )}

        {/* Image type tag top-right */}
        {/* {currentImg.type && (
          <div className="absolute top-3 right-3">
            <span
              className="text-[10px] font-medium px-2 py-1 rounded-lg capitalize"
              style={{ background: "rgba(0,0,0,0.45)", color: "#fff", backdropFilter: "blur(6px)" }}
            >
              {currentImg.type}
            </span>
          </div>
        )} */}
      </div>

      {/* Thumbnail strip */}
      {formattedImages.length > 1 && (
        <div
          className="flex gap-2 p-3 overflow-x-auto border-t scrollbar-hide"
          style={{ borderColor: "var(--border-c)" }}
        >
          {formattedImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setImgIdx(i)}
              className={cn(
                "shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all",
                i === imgIdx
                  ? "border-orange-500"
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
